// pages/product/model/import.jsx
import { useRef, useState } from "react"
import { Modal } from "components"
import axios from "axios"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { readSheet } from "read-excel-file/browser"
import { lang } from "config"

const CHUNK_SIZE = 500

const makeBatchId = () =>
    `B${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`

const parseExcelToObjects = async (file) => {
    const rows = await readSheet(file)
    if (!rows.length) return []

    const headers = rows[0].map((h) =>
        h === null || h === undefined ? "" : String(h).trim()
    )

    const objects = []
    for (let i = 1; i < rows.length; i++) {
        const r = rows[i]
        if (!r || r.every((c) => c === null || c === undefined || c === "")) {
            continue
        }
        const obj = {}
        for (let j = 0; j < headers.length; j++) {
            const key = headers[j]
            if (!key) continue
            obj[key] = r[j] ?? null
        }
        objects.push(obj)
    }
    return objects
}

const Content = ({ onSuccess }) => {
    const [file, setFile] = useState(null)
    const [errorMessages, setErrorMessages] = useState([])
    const [submitting, setSubmitting] = useState(false)
    const [progress, setProgress] = useState(null)
    const cancelRef = useRef(false)

    const resetState = () => {
        setErrorMessages([])
        setProgress(null)
        cancelRef.current = false
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!file) {
            toast.error("Silakan pilih file Excel terlebih dahulu")
            return
        }

        const url = `${process.env.REACT_APP_INVENTORY_API_URL}/product/model/import-batch`

        nProgress.start()
        setSubmitting(true)
        resetState()

        let allErrors = []
        let totalProcessed = 0
        let totalSkipped = 0

        try {
            const rows = await parseExcelToObjects(file)

            if (!rows.length) {
                toast.error("File Excel kosong atau tidak ada baris data")
                return
            }

            const totalChunks = Math.ceil(rows.length / CHUNK_SIZE)
            const batchId = makeBatchId()

            setProgress({
                chunk: 0,
                totalChunks,
                processedRows: 0,
                totalRows: rows.length,
            })

            for (let i = 0; i < totalChunks; i++) {
                if (cancelRef.current) {
                    toast("Impor dibatalkan", { icon: "⏹️" })
                    break
                }

                const start = i * CHUNK_SIZE
                const end = Math.min(start + CHUNK_SIZE, rows.length)
                const chunk = rows.slice(start, end)

                try {
                    const response = await axios.post(url, {
                        rows: chunk,
                        chunk_index: i,
                        total_chunks: totalChunks,
                        batch_id: batchId,
                    })

                    const data = response?.data || {}
                    totalProcessed += Number(data.processed || 0)
                    totalSkipped += Number(data.skipped || 0)

                    if (Array.isArray(data.errors) && data.errors.length) {
                        allErrors = allErrors.concat(
                            data.errors.map((err) => {
                                const excelRow =
                                    (err.chunk_index ?? i) * CHUNK_SIZE +
                                    (err.row_in_chunk ?? 0) +
                                    2 // +1 header, +1 1-based
                                const skuPart = err.sku ? ` SKU=${err.sku}` : ""
                                return `Row ${excelRow}${skuPart}: ${err.message}`
                            })
                        )
                    }
                } catch (error) {
                    const status = error?.response?.status
                    const data = error?.response?.data

                    if (status === 422 && data) {
                        const errs = Array.isArray(data?.errors)
                            ? data.errors
                            : typeof data?.errors === "object"
                            ? Object.values(data.errors).flat()
                            : [data?.message || "Validasi gagal."]
                        allErrors = allErrors.concat(
                            errs
                                .filter(Boolean)
                                .map((m) => `Chunk ${i + 1}: ${m}`)
                        )
                    } else {
                        allErrors.push(
                            `Chunk ${i + 1} (rows ${start + 2}-${end + 1}): ${
                                data?.message ||
                                error?.message ||
                                "Gagal mengirim chunk"
                            }`
                        )
                    }
                }

                setProgress({
                    chunk: i + 1,
                    totalChunks,
                    processedRows: end,
                    totalRows: rows.length,
                })
            }

            setErrorMessages(allErrors)

            if (allErrors.length === 0) {
                toast.success(
                    `Impor berhasil: ${totalProcessed} baris diproses${
                        totalSkipped ? `, ${totalSkipped} dilewati` : ""
                    }`
                )
                if (onSuccess) onSuccess()
            } else {
                toast.error(
                    `Impor selesai dengan ${allErrors.length} error. ${totalProcessed} baris berhasil diproses.`
                )
            }
        } catch (error) {
            toast.error(
                error?.message || "Gagal membaca / meng-impor file Excel"
            )
        } finally {
            nProgress.done()
            setSubmitting(false)
        }
    }

    const percent =
        progress && progress.totalRows
            ? Math.round((progress.processedRows / progress.totalRows) * 100)
            : 0

    return (
        <form onSubmit={handleSubmit} className="mt-6 space-y-6 text-xs">
            <div className="space-y-2">
                <p className="text-sm text-neutral-700">
                    Unggah file Excel dengan format:
                </p>
                <ul className="pl-5 space-y-1 list-disc text-[11px] text-neutral-600">
                    <li>
                        Header baris pertama minimal:
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            Model
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            Description
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            SKU
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            STOK
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            HARGA
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            BRG IP / LOKAL
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            LOKASI
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            berat/gram
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            Brand
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            Line
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            Category
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            Pre Order
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            SATUAN
                        </code>
                        ,
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            MIN PEMBELIAN
                        </code>
                    </li>
                    <li>
                        <code className="px-1 mx-1 rounded bg-neutral-100">
                            Line
                        </code>{" "}
                        Berisikan : Kacamata, Lensa, Accesories, Mesin
                    </li>
                    <li>
                        File diproses dalam batch{" "}
                        <span className="font-semibold">{CHUNK_SIZE}</span>{" "}
                        baris/request supaya backend tidak kehabisan memori.
                    </li>
                </ul>
            </div>

            <div className="space-y-2">
                <label className="block text-xs font-medium text-neutral-700">
                    File Excel
                </label>
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                        const f = e.target.files?.[0]
                        setFile(f || null)
                        resetState()
                    }}
                    disabled={submitting}
                    className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-neutral-800 file:text-white hover:file:bg-neutral-900 disabled:opacity-60"
                />
                {file && (
                    <p className="text-[11px] text-neutral-500">
                        File terpilih:{" "}
                        <span className="font-medium">{file.name}</span>
                    </p>
                )}
            </div>

            {progress && (
                <div className="p-3 space-y-2 text-[11px] border rounded-xl border-neutral-200 bg-neutral-50">
                    <div className="flex items-center justify-between">
                        <span className="font-medium text-neutral-700">
                            Chunk {progress.chunk} / {progress.totalChunks}
                        </span>
                        <span className="text-neutral-500">
                            {progress.processedRows} / {progress.totalRows} rows
                            ({percent}%)
                        </span>
                    </div>
                    <div className="w-full h-2 overflow-hidden rounded-full bg-neutral-200">
                        <div
                            className="h-full transition-all bg-neutral-800"
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                </div>
            )}

            {errorMessages.length > 0 && (
                <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
                    <div className="font-semibold mb-1">
                        {lang?.error || "Error"} ({errorMessages.length}):
                    </div>
                    <ul className="pl-4 space-y-1 list-disc">
                        {errorMessages.map((msg, idx) => (
                            <li key={idx}>{msg}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="flex items-center space-x-2">
                <button
                    type="submit"
                    disabled={submitting}
                    className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90 disabled:opacity-60"
                >
                    <span>
                        {submitting
                            ? progress
                                ? `Mengunggah ${progress.chunk}/${progress.totalChunks}...`
                                : "Membaca file..."
                            : "Impor Excel Product"}
                    </span>
                </button>
                {submitting && progress && (
                    <button
                        type="button"
                        onClick={() => {
                            cancelRef.current = true
                        }}
                        className="px-4 py-3 text-xs border border-neutral-300 text-neutral-700 rounded-xl hover:bg-neutral-100"
                    >
                        Batal
                    </button>
                )}
            </div>
        </form>
    )
}

const Import = ({ onSuccess }) => {
    const [isOpen, setIsOpen] = useState(false)

    const openModal = () => setIsOpen(true)
    const closeModal = () => setIsOpen(false)

    const handleSuccess = () => {
        closeModal()
        if (onSuccess) {
            onSuccess()
        }
    }

    return (
        <>
            <button
                type="button"
                onClick={openModal}
                className="inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    strokeWidth={1}
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path
                        stroke="none"
                        d="M0 0h24v24H0z"
                        fill="none"
                    ></path>
                    <path d="M4 4h16v4h-16z" />
                    <path d="M4 12h16v8h-16z" />
                    <path d="M10 8v8l3 -3l3 3v-8" />
                </svg>
                <span>Impor Excel</span>
            </button>

            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                title="Impor Product"
                content={<Content onSuccess={handleSuccess} />}
            />
        </>
    )
}

export default Import
